import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import api from "../../lib/api";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Badge } from "@/components/ui/Badge";
import DeleteConfirmDialog from "@/components/ui/DeleteConfirmDialog";
import MineTypeModal from "./MineTypeModal";
import LoadingSpinner from "@/components/ui/LoadingSpinner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/Table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/Select";
import {
  Plus,
  Search,
  Edit,
  Trash2,
  Filter,
  ChevronLeft,
  ChevronRight,
  Mountain,
  Building2,
} from "lucide-react";
import toast from "react-hot-toast";
import EditIcon from "@/components/icons/EditIcon";
import DeleteIcon from "@/components/icons/DeleteIcon";
import CloseIcon from "@/components/icons/CloseIcon";

const MineTypeIndexPage = () => {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [deleteDialog, setDeleteDialog] = useState({ isOpen: false, mineType: null });
  const [mineTypeModal, setMineTypeModal] = useState({
    isOpen: false,
    mineType: null,
    mode: "create",
  });
  const [isSearching, setIsSearching] = useState(false);

  const queryClient = useQueryClient();

  // Debounce search input
  useEffect(() => {
    if (search !== debouncedSearch) {
      setIsSearching(true);
    }

    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setIsSearching(false);
    }, 500);

    return () => clearTimeout(timer);
  }, [search, debouncedSearch]);

  const { data, isLoading, error } = useQuery(
    ["mine-types", { page, debouncedSearch, is_active: statusFilter }],
    async () => {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: "10",
        ...(debouncedSearch && { search: debouncedSearch }),
        ...(statusFilter && { is_active: statusFilter }),
      });
      const response = await api.get(`/mine-type?${params}`);
      return response.data;
    },
    {
      keepPreviousData: true,
    }
  );

  const deleteMutation = useMutation((mineTypeId) => api.delete(`/mine-type/${mineTypeId}`), {
    onSuccess: () => {
      queryClient.invalidateQueries(["mine-types"]);
      toast.success("Mine type deleted successfully");
      setDeleteDialog({ isOpen: false, mineType: null });
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Failed to delete mine type");
    },
  });

  const handleDeleteClick = (mineType) => {
    setDeleteDialog({
      isOpen: true,
      mineType: mineType,
    });
  };

  const handleDeleteConfirm = () => {
    if (deleteDialog.mineType) {
      deleteMutation.mutate(deleteDialog.mineType.id);
    }
  };

  const handleEditClick = async (mineType) => {
    await queryClient.refetchQueries(["mine-types"]);

    const updatedData = queryClient.getQueryData(["mine-types", { page, debouncedSearch, is_active: statusFilter }]);
    const updatedMineType = updatedData?.data?.find((mt) => mt.id === mineType.id) || mineType;

    setMineTypeModal({
      isOpen: true,
      mineType: updatedMineType,
      mode: "edit",
    });
  };

  const handleCreateClick = () => {
    setMineTypeModal({
      isOpen: true,
      mineType: null,
      mode: "create",
    });
  };

  const handleClearFilters = () => {
    setSearch("");
    setStatusFilter("");
    setPage(1);
  };

  const getStatusBadgeVariant = (isActive) => {
    return isActive ? "default" : "secondary";
  };

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  const handleStatusFilterChange = (value) => {
    setStatusFilter(value);
    setPage(1);
  };

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <p className="text-red-600">Failed to load mine types</p>
          <Button onClick={() => window.location.reload()} className="mt-2">
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <div className="">
        {/* Header */}
        <div className="flex flex-col gap-3 slide-up lg:flex-row lg:items-center lg:justify-between">
          <div className="slide-up">
            <Button
              onClick={handleCreateClick}
              className="w-full text-sm hover:bg-red-900 sm:w-auto sm:text-base"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Mine Type
            </Button>
          </div>
          {/* Filters */}
          <div className="flex flex-col w-full gap-3 lg:w-auto">
            <div className="flex flex-col w-full gap-3 sm:flex-row sm:items-end lg:justify-end">
              <div className="flex-1 min-w-0 sm:max-w-xs">
                <div className="relative">
                  <Input
                    placeholder="Search mine types..."
                    value={search}
                    onChange={handleSearchChange}
                    className="w-full pr-10"
                  />
                  <Search className="absolute w-4 h-4 ml-4 transform -translate-y-1/2 right-3 top-1/2 text-gray-0" />
                </div>
              </div>
              <div className="relative z-30 w-full sm:w-48">
                <Select value={statusFilter} onValueChange={handleStatusFilterChange}>
                  <SelectTrigger className="w-full text-sm focus:border-primary/40 sm:text-base">
                    <SelectValue placeholder="Filter by status">
                      {statusFilter === "true"
                        ? "Active"
                        : statusFilter === "false"
                          ? "Inactive"
                          : statusFilter === ""
                            ? "All Status"
                            : "Filter by status"}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent className="z-50 text-gray-400 shadow-lg bg-navy-100">
                    <SelectItem value="">All Status</SelectItem>
                    <SelectItem value="true">Active</SelectItem>
                    <SelectItem value="false">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={handleClearFilters}
                  className="w-full text-sm hover:bg-red-900 sm:w-auto sm:text-base"
                >
                  <CloseIcon className="w-5 h-5 mr-2" />
                  Clear
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Table */}
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <div className="w-8 h-8 mx-auto mb-4 border-4 border-blue-200 rounded-full animate-spin border-t-blue-600"></div>
              <p className="text-gray-600">Loading mine types...</p>
            </div>
          </div>
        ) : (
          <div className="shadow-sm slide-up backdrop-blur-sm">
            <Table>
              <TableHeader>
                <TableRow className="border-gray-200 hover:bg-gray-50/50">
                  <TableHead className="sticky md:sticky md:left-0 md:top-0">Name</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead className="text-center">Status</TableHead>
                  <TableHead className="text-center">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isSearching ? (
                  <TableRow>
                    <TableCell colSpan={7} className="py-12 text-center">
                      <div className="text-center">
                        <LoadingSpinner size="lg" />
                        <p className="mt-4 text-gray-600">Searching mine types...</p>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : !data?.data || data.data.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="py-12 text-center">
                      <div className="text-gray-500">
                        <Building2 className="w-12 h-12 mx-auto mb-4 opacity-50" />
                        <p>No mine types found</p>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  data?.data?.map((mineType) => (
                    <TableRow key={mineType.id} className="whitespace-nowrap">
                      <TableCell className="sticky md:sticky md:left-0 md:top-0">
                        <div className="p-4 my-2 mr-2 rounded-t-lg bg-navy-100">
                          <div className="text-gray-0">{mineType.name}</div>
                        </div>
                      </TableCell>
                      <TableCell className="max-w-80">
                        <div className="p-4 m-2 truncate rounded-t-lg bg-navy-100 text-gray-0 whitespace-nowrap ">
                          {mineType.description
                            ? mineType.description.length > 100
                              ? `${mineType.description.substring(0, 100)}...`
                              : mineType.description
                            : "No description"}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="p-4 m-2 text-center uppercase rounded-t-lg bg-navy-100 text-gray-0">
                          <Badge
                            variant={getStatusBadgeVariant(mineType.is_active)}
                            className={`border-none p-0 ${mineType.is_active ? "text-green-500" : "text-red-600"}`}
                          >
                            {mineType.is_active ? "Active" : "Inactive"}
                          </Badge>
                        </div>
                      </TableCell>
                      <TableCell className="w-4 text-right">
                        <div className="p-2 my-2 ml-2 text-center rounded-t-lg bg-navy-100 text-gray-0">
                          <div className="flex items-center justify-center gap-2">
                            <Button
                              variant="outline p-0"
                              size="sm"
                              className="rounded-lg"
                              onClick={() => handleEditClick(mineType)}
                            >
                              <EditIcon className="w-5 h-5 text-white transition-transform duration-200 hover:text-red-500 hover:scale-110" />
                            </Button>
                            <Button
                              variant="outline p-0"
                              size="sm"
                              className="hover:text-red-600"
                              onClick={() => handleDeleteClick(mineType)}
                              disabled={mineType.usage_count > 0}
                            >
                              <DeleteIcon className="w-5 h-5 text-red-500 transition-transform duration-200 hover:scale-110" />
                            </Button>
                          </div>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            {/* Pagination */}
            <div className="flex flex-col pr-0 border-t border-b border-gray-0 sm:flex-row sm:items-center sm:justify-between sm:pr-12">
              <div className="py-4 text-sm text-center text-gray-400 uppercase sm:text-left">
                <span className="block sm:inline">
                  Showing {(page - 1) * 10 + 1} to{" "}
                  {Math.min(page * 10, data?.pagination?.total || 0)} of{" "}
                  {data?.pagination?.total || 0} results
                </span>
              </div>
              <div className="flex items-center justify-center w-full sm:w-auto">
                <Button
                  variant="outline"
                  className="flex h-auto min-h-[44px] flex-1 items-center justify-center rounded-none rounded-b-none border-r border-gray-0 p-3 text-xs font-normal uppercase text-gray-0 hover:scale-100 sm:min-h-[52px] sm:flex-none sm:p-5"
                  size="sm"
                  onClick={() => setPage(page - 1)}
                  disabled={page <= 1}
                >
                  <ChevronLeft className="w-4 h-4 mr-2 text-red-500" />
                  PREVIOUS
                </Button>
                <Button
                  variant="outline"
                  className="flex h-auto min-h-[44px] flex-1 items-center justify-center rounded-none rounded-b-none border-r border-gray-0 p-3 text-xs font-normal uppercase text-gray-0 hover:scale-100 sm:min-h-[52px] sm:flex-none sm:p-5"
                >
                  {page}
                </Button>
                <Button
                  variant="outline"
                  className="flex h-auto min-h-[44px] flex-1 items-center justify-center rounded-none rounded-b-none border-0 p-3 text-xs font-normal uppercase text-gray-0 hover:scale-100 sm:min-h-[52px] sm:flex-none sm:p-5"
                  size="sm"
                  onClick={() => setPage(page + 1)}
                  disabled={page >= data?.pagination?.pages}
                >
                  NEXT
                  <ChevronRight className="w-4 h-4 ml-2 text-red-500" />
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Mine Type Modal */}
        <MineTypeModal
          isOpen={mineTypeModal.isOpen}
          onClose={() => setMineTypeModal({ isOpen: false, mineType: null, mode: "create" })}
          mineType={mineTypeModal.mineType}
          mode={mineTypeModal.mode}
        />

        {/* Delete Confirmation Dialog */}
        <DeleteConfirmDialog
          isOpen={deleteDialog.isOpen}
          onClose={() => setDeleteDialog({ isOpen: false, mineType: null })}
          onConfirm={handleDeleteConfirm}
          title="Delete Mine Type"
          userName={deleteDialog.mineType?.name || ""}
          isLoading={deleteMutation.isLoading}
        />
      </div>
    </div>
  );
};

export default MineTypeIndexPage;
